import { DatePipe, DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Badge } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { Divider } from 'primeng/divider';
import { Editor } from 'primeng/editor';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, finalize, merge, of, Subject, Subscription, take, takeUntil, timer } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TypesEnum } from '../../../../core/enums/shared-type.enum';
import { Directory, DirectoryRequest } from '../../../../core/interfaces/directory.interface';
import { IFile, 
    getFileIcon,
    getFileTypeColor,
    getFileTypeLabel } from '../../../../core/interfaces/file.interface';
import { DirectoryService } from '../../../../core/services/directory.service';
import { FileService } from '../../../../core/services/file.service';
import { NavigationService } from '../../../../core/services/navigation.service';
import { SharedService } from '../../../../core/services/shared.service';
import { DirectoryStateService } from '../../../../core/services/state/directory-state.service';
import { DirectoryDialogComponent } from '../../../admin/storage/directory/directory-dialog/directory-dialog.component';
import { DirectoryListComponent } from './directory-list/directory-list.component';
import { FileListComponent } from './file-list/file-list.component';
import { LayoutService } from '../../../../layout/service/layout.service';

export function noSpecialCharsValidator(control: any) {
    const value = control.value;
    if (!value) return null;
    const hasSpecialChars = /[<>:"/\\|?*]/.test(value);
    return hasSpecialChars ? { specialChars: true } : null;
}

export enum ViewMode {
    LIST = 0,
    GRID = 1
}

export enum ContentDestination {
    EDITOR = 'editor', // Deve abrir no ONLYOFFICE
    VIEWER = 'viewer', // Deve abrir em um visualizador (ou nova aba)
    TEXT = 'text', // Deve abrir em um visualizador (ou nova aba)
    UNKNOWN = 'unknown' // Tipo não suportado
}

const EDITOR_TYPE_MAPPING: { [key: string]: ContentDestination } = {
    // ===== ONLYOFFICE (Editor) =====
    'application/pdf': ContentDestination.EDITOR,

    'application/msword': ContentDestination.EDITOR,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ContentDestination.EDITOR,
    'application/rtf': ContentDestination.EDITOR,

    'application/vnd.ms-excel': ContentDestination.EDITOR,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ContentDestination.EDITOR,
    'text/csv': ContentDestination.EDITOR,

    'application/vnd.ms-powerpoint': ContentDestination.EDITOR,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ContentDestination.EDITOR,

    // ===== Imagens (Viewer simples) =====
    'image/png': ContentDestination.VIEWER,
    'image/jpeg': ContentDestination.VIEWER,
    'image/jpg': ContentDestination.VIEWER,
    'image/gif': ContentDestination.VIEWER,
    'image/webp': ContentDestination.VIEWER,

    // ===== Text-only =====
    'text/plain': ContentDestination.TEXT,
    'text/ini': ContentDestination.TEXT,
    'text/log': ContentDestination.TEXT
};

@Component({
    selector: 'app-shared-directory',
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        DecimalPipe,
        ButtonModule,
        DialogModule,
        ProgressSpinnerModule,
        PaginatorModule,
        TooltipModule,
        ProgressBarModule,
        DirectoryListComponent,
        FileListComponent,
        DirectoryDialogComponent,
        ReactiveFormsModule,
        NgClass,
        Divider,
        FormsModule,
        Editor,
        Badge,
        ConfirmDialog,
        Toast,
        DatePipe
    ],
    templateUrl: './shared-directory.component.html',
    styleUrl: './shared-directory.component.scss',
    providers: [MessageService, ConfirmationService]
})
export class SharedDirectoryComponent implements OnInit, OnDestroy {

    protected readonly getFileIcon = getFileIcon;
    protected readonly getFileTypeColor = getFileTypeColor;
    protected readonly getFileTypeLabel = getFileTypeLabel;
    
    private readonly layoutService = inject(LayoutService);

    ViewMode = { LIST: 0, GRID: 1 } as const;
    viewMode: any = this.layoutService.layoutConfig().viewMode ?? ViewMode.LIST;

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    private stopPolling$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    private readonly directoryService = inject(DirectoryService);
    private readonly sharedService = inject(SharedService);
    private readonly fileService = inject(FileService);
    private readonly messageService = inject(MessageService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly selectedDirectoryService = inject(DirectoryStateService);
    private readonly router = inject(Router);
    protected readonly navService = inject(NavigationService);
    protected readonly confirmationService = inject(ConfirmationService);
    private loadFilesSubject = new Subject<any>();

    @HostListener('contextmenu', ['$event'])
    onContextMenu(event: MouseEvent): void {
        event.preventDefault();
    }

    //Paginação (arquivos e pastas)
    first: number = 0;
    rows: number = 10;
    totalRecords = 0;
    filesFirst: number = 0;
    filesRows: number = 10;
    filesTotalRecords = 0;

    selectedDirectoryId: string | null = null;
    selectedDirectory: Directory | null = null;
    breadcrumb: Directory[] = [];

    files$: IFile[] = [];
    shareds$ = signal<Directory[]>([]);
    selectedFiles: IFile[] = [];
    isBulkDeleting = false;

    isLoading = false;
    isLoadingFiles = false;
    isLoadingImage = false;
    uploading = false;

    uploadProgress: { name: string; progress: number; error?: boolean; errorMessage?: string }[] = [];
    private uploadSubscriptions: Subscription[] = [];

    directoryForm!: FormGroup;
    isEditMode = false;
    directoryEditing: Directory | null = null;
    directoryDialog = false;

    dirPropsDialog = false;
    dirPropsTarget: Directory | null = null;
    filePropsDialog = false;
    filePropsTarget: IFile | null = null;

    moveDialog = false;
    moveTargetType: 'file' | 'directory' | null = null;
    moveTargetItem: Directory | IFile | null = null;
    moveDestinationId: string | null = null;
    moveDestinationIsRoot = false;
    moveDestinationName = 'Selecione uma pasta';
    moveBrowsingDirectoryId: string | null = null;
    moveBrowsingDirectories: Array<Directory & { nodeId?: string; sharedResourceId?: string }> = [];
    moveBrowsingLoading = false;
    moveBreadcrumb: Array<{ id: string | null; name: string }> = [{ id: null, name: 'Raiz' }];

    ngOnInit() {
        this.initializeForm();

        this.navService.navigation$
            .pipe(
                // 1. Evita disparos múltiplos se o stack for o mesmo (compara o ID do último item)
                distinctUntilChanged((prev, curr) => {
                    return prev.at(-1)?.id === curr.at(-1)?.id;
                }),
                // 2. Dá um pequeno fôlego para o estado da navegação estabilizar
                debounceTime(50),
                takeUntil(this.destroy$)
            )
            .subscribe((stack) => {
                this.breadcrumb = stack;
                const current: any = stack.at(-1);

                this.selectedDirectoryId = current.id === 'root' ? null : current.id;

                // Resetar estados antes de carregar para evitar inconsistência visual
                this.isLoading = true;
                this.isLoadingFiles = true;

                // Chamamos os métodos de carga
                this.loadShareds({ first: 0, rows: this.rows });

                if (this.selectedDirectoryId) {
                    return;
                } else {
                    this.files$ = [];
                    this.filesTotalRecords = 0;
                    this.isLoadingFiles = false;
                }
            });

        this.loadFilesSubject
            .pipe(
                // Se vierem 2 pedidos de carga em menos de 100ms, faz só um
                debounceTime(100),
                // Se um novo pedido chegar antes do anterior acabar, cancela o anterior no Backend
                switchMap((event) => {
                    this.isLoadingFiles = true;

                    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.filesRows)) + 1;

                    this.filesFirst = event.first ?? 0;
                    this.filesRows = event.rows ?? this.filesRows;

                    return this.fileService.getFilesByDirectory(this.selectedDirectoryId?.toString() ?? '', false, { page, limit: event.rows ?? this.filesRows }).pipe(
                        finalize(() => (this.isLoadingFiles = false)),
                        catchError(() => of({ content: [], totalElements: 0 }))
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((response) => {
                this.files$ = response.content || [];
                this.filesTotalRecords = response.totalElements || 0;
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopPolling$.next();
        this.stopPolling$.complete();
    }

    onDirectorySelect(directory: Directory) {
        this.selectedDirectory = directory;
        this.selectedDirectoryService.setSelected(directory);
        this.navService.push(directory);
    }

    goBack() {
        this.navService.pop();
        this.selectedDirectoryService.setSelected(this.navService.current);
    }

    navigateToBreadcrumb(directory: Directory) {
        this.selectedDirectory = directory;
        this.selectedDirectoryService.setSelected(directory);
        this.navService.navigateTo(directory.id);
    }

    getBreadcrumbPath(): Directory[] {
        return this.breadcrumb.length ? this.breadcrumb : [{ id: 'root', name: 'Meus Compartilhamentos' } as Directory];
    }

    triggerFileSelect() {
        const input = this.resetFileInput(this.fileInput?.nativeElement ?? document.querySelector<HTMLInputElement>('input[type=file]'));

        if (!input) {
            return;
        }

        input.click();
    }

    /**
     * Envia arquivos selecionados para upload
     * @param event
     */
    async onUploadSelectedFiles(event: Event) {
        const input = event.target as HTMLInputElement;
        const fileList = input.files;

        if (!fileList || fileList.length === 0) return;

        const files = Array.from(fileList);
        const currentDirectory = this.selectedDirectory;

        if (!currentDirectory) return;

        console.log('📂 Diretório atual:', currentDirectory);

        this.uploading = true;

        this.uploadProgress = files.map((file) => ({
            name: file.name,
            progress: 0,
            error: false,
            errorMessage: undefined
        }));

        try {
            const actualPath = currentDirectory.alias ?? currentDirectory.name;
            /**
             * 1️⃣ Pré-autoriza upload
             */
            const presignedResponse = await this.fileService.preAuthorizeUpload(actualPath, files, currentDirectory).toPromise();

            /**
             * ⚠️ Validação crítica
             */
            if (!presignedResponse || !Array.isArray(presignedResponse.uploadUrls) || presignedResponse.uploadUrls.length === 0) {
                throw new Error('Falha ao obter URLs de upload');
            }

            if (presignedResponse.uploadUrls.length !== files.length) {
                console.warn('⚠️ Quantidade de URLs diferente da quantidade de arquivos', presignedResponse, files);
            }

            /**
             * 2️⃣ Upload dos arquivos
             */
            const uploads = presignedResponse.uploadUrls.map(async (item: any, index: number) => {
                const file = files[index];

                // Ajuste na validação: o item deve existir.
                // Se for multipart, ele não terá 'url' na raiz, mas terá 'urls' (plural)
                if (!file || !item) return;

                console.log(`🚀 Iniciando upload para ${item.key.split('/').pop()}`);

                try {
                    await this.fileService.executeUpload(
                        item, // <-- PASSAMOS O OBJETO INTEIRO AQUI
                        file,
                        { id: currentDirectory.id, path: actualPath },
                        (percent) => {
                            this.uploadProgress[index].progress = percent;
                        }
                    );
                    this.uploadProgress[index].progress = 100;
                } catch (error: any) {
                    this.uploadProgress[index].error = true;
                    this.uploadProgress[index].errorMessage = error?.message || 'Erro no upload';
                }
            });

            const results = await Promise.allSettled(uploads);

            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    this.uploadProgress[index].error = true;
                    this.uploadProgress[index].errorMessage = result.reason?.message ?? 'Falha no upload';
                }
            });
        } catch (error: any) {
            console.error('❌ Erro no fluxo de upload:', error);

            this.uploadProgress.forEach((p) => {
                p.error = true;
                p.errorMessage = error?.message || 'Erro ao preparar upload';
            });
        } finally {
            this.uploading = false;
            this.checkAllUploadsDone();
            this.resetFileInput(input);
        }
    }

    async onUploadSelectedDirectory(files: File[]) {
        if (!files.length || !this.selectedDirectory) return;

        const currentDirectory = this.selectedDirectory;
        const actualPath = currentDirectory.alias ?? currentDirectory.name;

        // 1️⃣ Adicionamos os novos arquivos à lista de progresso global (sem resetar a anterior)
        const newProgressEntries = files.map((file) => ({
            name: file.webkitRelativePath || file.name,
            progress: 0,
            error: false
        }));

        // Se não estava fazendo upload, ativa o modal e limpa a lista antiga
        if (!this.uploading) {
            this.uploading = true;
            this.uploadProgress = [...newProgressEntries];
        } else {
            // Se já estava subindo, apenas concatena os novos arquivos no final da lista
            this.uploadProgress = [...this.uploadProgress, ...newProgressEntries];
        }

        try {
            // 2️⃣ Pré-autoriza o lote atual (o chunk)
            const presignedResponse = await this.fileService.preAuthorizeUpload(actualPath, files, currentDirectory).toPromise();

            if (!presignedResponse?.uploadUrls?.length) throw new Error('URLs não geradas');

            /**
             * 3️⃣ Upload dos arquivos do lote
             */
            const uploads = presignedResponse.uploadUrls.map(async (item: any, index: number) => {
                const file = files[index];
                if (!file || !item) return;

                // Identificador único para encontrar o item correto na lista global de progresso
                const fileName = file.webkitRelativePath || file.name;

                try {
                    await this.fileService.executeUpload(item, file, { id: currentDirectory.id, path: actualPath }, (percent) => {
                        // Localiza o item correto na lista global pelo nome
                        const progressItem = this.uploadProgress.find((p) => p.name === fileName);
                        if (progressItem) {
                            progressItem.progress = percent;
                        }
                    });

                    const finalItem = this.uploadProgress.find((p) => p.name === fileName);
                    if (finalItem) finalItem.progress = 100;
                } catch (error: any) {
                    const errorItem = this.uploadProgress.find((p) => p.name === fileName);
                    if (errorItem) {
                        errorItem.error = true;
                        errorItem.errorMessage = error?.message || 'Erro no upload';
                    }
                }
            });

            // Espera o lote atual terminar
            await Promise.allSettled(uploads);
        } catch (error: any) {
            // Erro na pré-autorização afeta apenas o lote atual
            files.forEach((file) => {
                const fileName = file.webkitRelativePath || file.name;
                const item = this.uploadProgress.find((p) => p.name === fileName);
                if (item) {
                    item.error = true;
                    item.errorMessage = error?.message || 'Falha no lote';
                }
            });
        } finally {
            // Verificamos se todos os arquivos (incluindo chunks anteriores) terminaram
            this.checkAllUploadsDone();
        }
    }

    getRelativeDirectoryPath(file: File): string {
        const relativePath = file.webkitRelativePath;

        if (!relativePath) {
            return '';
        }

        const lastSlash = relativePath.lastIndexOf('/');

        return lastSlash !== -1 ? relativePath.substring(0, lastSlash) : '';
    }

    cancelUploads() {
        this.uploadSubscriptions.forEach((sub) => sub.unsubscribe());
        this.uploadSubscriptions = [];
    }

    private resetFileInput(input?: HTMLInputElement | null): HTMLInputElement | null {
        const target = input ?? this.fileInput?.nativeElement ?? document.querySelector<HTMLInputElement>('input[type=file]');

        if (!target) {
            return null;
        }

        if (target.type === 'file') {
            target.value = '';

            const parent = target.parentElement;
            if (parent) {
                const replacement = this.createFileInput();
                parent.replaceChild(replacement, target);
                this.fileInput = new ElementRef<HTMLInputElement>(replacement);
                return replacement;
            }
        }

        return target;
    }

    private createFileInput(): HTMLInputElement {
        const replacement = document.createElement('input');
        replacement.type = 'file';
        replacement.multiple = true;
        replacement.hidden = true;
        replacement.addEventListener('change', (event: Event) => this.onUploadSelectedFiles(event));
        return replacement;
    }

    private checkAllUploadsDone() {
        const checkInterval = setInterval(() => {
            const allDone = this.uploadProgress.every((progress) => progress.progress === 100 || progress.error);
            if (allDone) {
                clearInterval(checkInterval);
                setTimeout(() => {
                    this.uploading = false;
                    this.loadShareds({ first: 0, rows: this.rows });
                }, 1000);
            }
        }, 500);
    }

    onCreateDirectory(isEditing?: boolean, directory?: Directory) {
        this.directoryEditing = null;
        this.isEditMode = isEditing ?? false;
        this.directoryForm.reset();

        if (this.isEditMode && directory) {
            this.directoryForm.patchValue(directory);
            this.directoryEditing = directory;
        }

        this.directoryDialog = true;
    }

    saveDirectory($event: String | null | undefined) {
        console.log('Saving directory...', $event);
        if (this.directoryForm.valid) {
            const directoryRequest: DirectoryRequest = {
                nodeType: TypesEnum.DIRECTORY,
                id: this.isEditMode ? this.directoryEditing?.id : undefined,
                name: this.directoryForm.value.name,
                parentId: this.selectedDirectoryId || undefined
            };

            this.directoryService.save(directoryRequest).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: `Diretório ${this.isEditMode ? 'renomeado' : 'criado'} com sucesso`
                    });
                    this.directoryDialog = false;
                    this.loadShareds({ first: 0, rows: this.rows });
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: `Erro ao ${this.isEditMode ? 'renomear' : 'criar'} diretório`
                    });
                }
            });
        }
    }

    cancelDirectory() {
        this.directoryDialog = false;
        this.directoryForm.reset();
    }

    onCreateSubdirectoryFromContext(directory: Directory) {
        this.selectedDirectoryId = directory.id;
        this.onCreateDirectory();
    }

    onRenameDirectorySelected(directory: Directory) {
        this.onCreateDirectory(true, directory);
    }

    onMoveDirectorySelected(directory: Directory) {
        this.openMoveDialog('directory', directory);
    }

    onMoveFileSelected(file: IFile) {
        this.openMoveDialog('file', file);
    }

    openMoveDialog(type: 'file' | 'directory', item: Directory | IFile) {
        this.moveTargetType = type;
        this.moveTargetItem = item;
        this.moveDialog = true;
        this.moveDestinationId = null;
        this.moveDestinationName = 'Selecione uma pasta';
        this.moveBreadcrumb = [{ id: null, name: 'Raiz' }];
        this.moveBrowsingDirectoryId = null;
        this.loadMoveDirectories();
    }

    loadMoveDirectories() {
        this.moveBrowsingLoading = true;

        if (!this.moveBrowsingDirectoryId) {
            this.sharedService
                .getShareds({ page: 1, limit: 100 })
                .pipe(take(1), finalize(() => (this.moveBrowsingLoading = false)))
                .subscribe({
                    next: (response: any) => {
                        this.moveBrowsingDirectories = (response.content || [])
                            .map((item: any) => ({
                                id: item.id,
                                nodeId: item.node?.id,
                                sharedResourceId: item.resourceId,
                                name: item.node?.name || item.name || item.alias || 'Sem nome',
                                alias: item.alias,
                                parentId: item.node?.parentId ?? null,
                                shareable: item.node?.shareable || false,
                                tenancy: item.node?.tenantId,
                                hasChildren: item.node?.hasChildren,
                                children: item.children || []
                            }))
                            .filter((directory: { id: string | undefined; }) => directory.id !== this.moveTargetItem?.id);
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: 'Não foi possível carregar as pastas de destino.'
                        });
                    }
                });

            return;
        }

        this.directoryService
            .getDirectories({
                parentId: this.moveBrowsingDirectoryId,
                page: 1,
                limit: 100,
                shareable: false
            })
            .pipe(take(1), finalize(() => (this.moveBrowsingLoading = false)))
            .subscribe({
                next: (response: any) => {
                    this.moveBrowsingDirectories = (response.content || [])
                        .map((item: any) => ({
                            id: item.id,
                            name: item.name || item.alias || 'Sem nome',
                            alias: item.alias,
                            parentId: item.parentId,
                            shareable: item.shareable || false,
                            tenancy: item.tenantId,
                            hasChildren: item.hasChildren,
                            children: item.children
                        }))
                        .filter((directory: { id: string | undefined; }) => directory.id !== this.moveTargetItem?.id);
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível carregar as pastas de destino.'
                    });
                }
            });
    }

    navigateMoveBreadcrumb(index: number) {
        const target = this.moveBreadcrumb[index];
        this.moveBreadcrumb = this.moveBreadcrumb.slice(0, index + 1);
        this.moveBrowsingDirectoryId = target?.id ?? null;
        this.moveDestinationId = null;
        this.moveDestinationIsRoot = target?.id === null;
        this.moveDestinationName = target?.id === null ? 'Raiz' : 'Selecione uma pasta';
        this.loadMoveDirectories();
    }

    openMoveDirectory(directory: Directory) {
        this.moveBreadcrumb = [...this.moveBreadcrumb, { id: directory.id, name: directory.name }];
        this.moveBrowsingDirectoryId = directory.id;
        this.moveDestinationId = null;
        this.moveDestinationIsRoot = false;
        this.moveDestinationName = 'Selecione uma pasta';
        this.loadMoveDirectories();
    }

    selectMoveDestination(directory: Directory & { nodeId?: string; sharedResourceId?: string }) {
        // Para diretórios raiz compartilhados, o ID correto é o ID do recurso compartilhado (item.id).
        // Para subpastas, o ID correto continua sendo o próprio node id.
        this.moveDestinationId = directory.id;
        this.moveDestinationIsRoot = false;
        this.moveDestinationName = directory.name;
    }

    confirmMove() {
        if (!this.moveTargetItem || (!this.moveDestinationId && !this.moveDestinationIsRoot)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Destino obrigatório',
                detail: 'Selecione uma pasta válida para concluir a movimentação.'
            });
            return;
        }

        const nodeType = this.moveTargetType === 'directory' ? TypesEnum.DIRECTORY : undefined;
        const destinationId = this.moveDestinationIsRoot ? '' : this.moveDestinationId || '';

        this.directoryService.moveNode(this.moveTargetItem.id, destinationId || '', nodeType).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: `${this.moveTargetType === 'directory' ? 'Diretório' : 'Arquivo'} movido com sucesso.`
                });
                this.closeMoveDialog();
                this.loadShareds({ first: 0, rows: this.rows });
            },
            error: (error: any) => {
                console.error('Erro ao mover item:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível mover o item para a pasta selecionada.'
                });
            }
        });
    }

    closeMoveDialog() {
        this.moveDialog = false;
        this.moveTargetType = null;
        this.moveTargetItem = null;
        this.moveDestinationId = null;
        this.moveDestinationName = 'Selecione uma pasta';
        this.moveBreadcrumb = [{ id: null, name: 'Raiz' }];
        this.moveBrowsingDirectoryId = null;
        this.moveBrowsingDirectories = [];
    }

    onRenameFileSelected(file: { file: IFile; newName: string }) {
        this.fileService.renameFile(file.file.id, file.newName).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: `Arquivo renomeado para ${file.newName}`
                });
                this.loadFiles({ first: this.filesFirst, rows: this.filesRows });
            },
            error: (error: any) => {
                console.error('Erro ao renomear arquivo:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível renomear o arquivo'
                });
            }
        });
    }

    onUploadFileToDirectory(directory: Directory) {
        this.selectedDirectoryId = directory.id;
        this.triggerFileSelect();
    }

    openDirectoryProperties(directory: Directory) {
        this.dirPropsTarget = directory;
        this.dirPropsDialog = true;
    }

    onDownloadFile(file: IFile) {
        this.messageService.add({
            severity: 'info',
            summary: 'Em desenvolvimento',
            detail: 'Baixar arquivo será implementado em breve.'
        });
    }

    onMoveDirectoryToRecycle(directory: Directory) {
        this.confirmationService.confirm({
            message: `Tem certeza que deseja mover "<strong>${directory.name}</strong>" para a lixeira?`,
            header: 'Confirmar Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Mover para Lixeira',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger p-button-text font-bold',
            rejectButtonStyleClass: 'p-button-secondary p-button-text',
            accept: () => {
                // 1️⃣ executa a ação
                this.fileService.recycleOrRestoreItem(directory.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: `O diretório "${directory.name}" foi movido para a lixeira.`
                        });
                        this.loadShareds({ first: Math.floor(this.first / this.rows) + 1, rows: this.rows });
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: `Erro ao mover o diretório "${directory.name}" para a lixeira.`
                        });
                    }
                });

                // 2️⃣ fecha o dialog explicitamente
                this.confirmationService.close();
            },

            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    onMoveFileToRecycle(file: IFile) {
        this.confirmationService.confirm({
            message: `Tem certeza que deseja mover "<strong>${file.name}</strong>" para a lixeira?`,
            header: 'Confirmar Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Mover para Lixeira',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger p-button-text font-bold',
            rejectButtonStyleClass: 'p-button-secondary p-button-text',
            accept: () => {
                // 1️⃣ executa a ação
                this.fileService.recycleOrRestoreItem(file.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: `O arquivo "${file.name}" foi movido para a lixeira.`
                        });
                        this.loadFiles({ page: Math.floor(this.first / this.filesRows) + 1, rows: this.filesRows });
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: `Erro ao mover o arquivo "${file.name}" para a lixeira.`
                        });
                    }
                });

                // 2️⃣ fecha o dialog explicitamente
                this.confirmationService.close();
            },

            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    openFileProperties(file: IFile) {
        console.log(file);
        this.filePropsTarget = file;
        this.filePropsDialog = true;
    }

    get isAllFilesSelected(): boolean {
        return this.files$.length > 0 && this.selectedFiles.length === this.files$.length;
    }

    onSelectedFilesChange(files: IFile[]) {
        this.selectedFiles = files;
    }

    selectAllFiles() {
        this.selectedFiles = [...this.files$];
    }

    clearSelectedFiles() {
        this.selectedFiles = [];
    }

    bulkDeleteSelectedFiles() {
        if (this.selectedFiles.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Seleção vazia',
                detail: 'Selecione ao menos um arquivo para excluir.'
            });
            return;
        }

        this.confirmationService.confirm({
            message: `Tem certeza que deseja mover ${this.selectedFiles.length} arquivo(s) para a lixeira?`,
            header: 'Confirmar Exclusão em Massa',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Mover para Lixeira',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger p-button-text font-bold',
            rejectButtonStyleClass: 'p-button-secondary p-button-text',
            accept: () => {
                this.executeBulkDelete();
                this.confirmationService.close();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    private executeBulkDelete() {
        this.isBulkDeleting = true;
        const selected = [...this.selectedFiles];

        selected.forEach((file) => {
            this.fileService.recycleOrRestoreItem(file.id).pipe(take(1)).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: `O arquivo "${file.name}" foi movido para a lixeira.`
                    });
                    this.files$ = this.files$.filter((item) => item.id !== file.id);
                    this.filesTotalRecords -= 1;
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: `Erro ao mover o arquivo "${file.name}" para a lixeira.`
                    });
                },
                complete: () => {
                    this.selectedFiles = this.selectedFiles.filter((item) => item.id !== file.id);
                    if (this.selectedFiles.length === 0) {
                        this.isBulkDeleting = false;
                    }
                }
            });
        });
    }

    toggleView(mode: number) {
        this.viewMode = mode;
        this.layoutService.saveViewMode(mode);
    }

    async loadShareds(event: { first?: number; rows?: number; shareable?: boolean }) {
        this.isLoading = true;

        this.first = event.first ?? this.first ?? 0;
        this.rows = event.rows ?? this.rows ?? 10;

        const page = Math.floor(this.first / this.rows) + 1;

        if (this.selectedDirectoryId) {
            await this.loadDirectories(page, this.rows, false);
            await this.loadFiles({ first: 0, rows: this.filesRows });
            return;
        }

        this.sharedService
            .getShareds({ page, limit: this.rows })
            .pipe(
                take(1),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (response: any) => {
                    const directories: Directory[] = (response.content || []).map((item: any) => ({
                        id: item.id,
                        name: item.name || item.alias || 'Sem nome',
                        alias: item.alias,
                        parentId: item.node?.parentId ?? null,
                        shareable: item.node?.shareable || false,
                        tenancy: item.node?.tenantId,
                        hasChildren: item.node?.hasChildren || false,
                        sharedGroup: item.sharedGroup,
                        children: item.children || []
                    }));

                    this.shareds$.set(directories);
                    this.totalRecords = Number(
                        response.totalElements ?? response.total ?? response.pagination?.totalRows ?? response.totalRecords ?? 0
                    ) || 0;
                    this.isLoading = false;
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Erro ao carregar diretórios'
                    });
                    this.isLoading = false;
                }
            });
    }

    async loadDirectories(page: number, limit: number, shareable: boolean) {
        this.isLoading = true;

        this.directoryService
            .getDirectories({
                parentId: this.selectedDirectoryId,
                page,
                limit,
                shareable
            })
            .pipe(
                take(1),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (response: any) => {
                    const directories: Directory[] = (response.content || []).map((item: any) => ({
                        id: item.id,
                        name: item.name || item.alias || 'Sem nome',
                        alias: item.alias,
                        parentId: item.parentId,
                        shareable: item.shareable || false,
                        tenancy: item.tenantId,
                        hasChildren: item.hasChildren,
                        children: item.children
                    }));

                    this.shareds$.set(directories);
                    this.totalRecords = response.totalElements || 0;
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Erro ao carregar diretórios'
                    });
                }
            });
    }

    loadFiles(event: any) {
        if (!this.selectedDirectoryId) return;
        this.loadFilesSubject.next(event);
    }

    // loadFiles(event: any) {
    //     if (!this.selectedDirectoryId) return;

    //     this.isLoadingFiles = true;

    //     this.filesFirst = event.first ?? 0;
    //     this.filesRows = event.rows ?? this.filesRows;

    //     const page = Math.floor(this.filesFirst / this.filesRows) + 1;

    //     this.fileService
    //         .getFilesByDirectory(
    //             this.selectedDirectoryId,
    //             false,
    //             { page, limit: this.filesRows }
    //         )
    //         .pipe(
    //             take(1),
    //             finalize(() => this.isLoadingFiles = false)
    //         )
    //         .subscribe({
    //             next: (response: any) => {
    //                 this.files$ = response.content || [];
    //                 this.filesTotalRecords = response.totalElements || 0;
    //                 // this.startPollingIfNecessary();
    //             },
    //             error: (err) => {
    //                 this.messageService.add({
    //                     severity: 'error',
    //                     summary: 'Erro',
    //                     detail: `Erro ao carregar arquivos ${err?.message || ''}`
    //                 });
    //             }
    //         });
    // }

    displayImageModal: boolean = false;
    imageBase64DataUrl: string | null = null;
    imageName: string = '';

    onFileSelect(file: IFile): void {
        const fileType = file.contentType.toLowerCase();

        // 1. Mapeamento Direto
        const destination = EDITOR_TYPE_MAPPING[fileType] || ContentDestination.UNKNOWN;

        if (destination === ContentDestination.EDITOR) {
            // Abre o ONLYOFFICE
            this.router.navigate(['/default/files', file.s3Key, 'editor']);
            return;
        }

        if (destination === ContentDestination.VIEWER || destination === ContentDestination.TEXT) {
            this.isLoadingImage = true;
            this.fileService.viewFile(String(file.s3Key), '').subscribe({
                next: (response: any) => {
                    try {
                        const { base64 } = response;

                        const contentType = file.s3Key.endsWith('.png') ? 'image/png' : file.name.endsWith('.jpg') || file.name.endsWith('.jpeg') ? 'image/jpeg' : 'application/octet-stream';

                        if (!base64) {
                            throw new Error('Arquivo inválido ou vazio');
                        }

                        // Cria um Blob a partir do Base64
                        const byteCharacters = atob(base64);

                        if (!contentType.startsWith('image')) {
                            this.viewTextFile(byteCharacters, file.s3Key);
                            this.isLoadingImage = false;
                            return;
                        }

                        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: contentType || 'application/octet-stream' });

                        // Gera uma URL temporária
                        const url = URL.createObjectURL(blob);

                        // Abre em nova aba
                        // window.open(url, '_blank');
                        this.imageName = file.name;
                        this.imageBase64DataUrl = url;
                        this.displayImageModal = true;
                        // Libera memória após um tempo (boa prática)
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                    } catch (err) {
                        console.error('Erro ao abrir imagem:', err);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: 'Erro ao visualizar imagem'
                        });
                    }
                    this.isLoadingImage = false;
                },
                error: (error: any) => {
                    console.error('Erro ao visualizar imagem:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Erro ao visualizar imagem'
                    });
                    this.isLoadingImage = false;
                }
            });
        }
    }

    fileContent: string = '';
    fileName: string = '';
    displayTextViewer: boolean = false;

    viewTextFile(fileData: string, name: string): void {
        this.isLoading = true;
        try {
            this.fileName = name;
            // Tenta converter a string "maluca" de volta para bytes e redecodificar como UTF-8
            const decodedContent = decodeURIComponent(escape(fileData));

            // Agora aplica a lógica de parágrafos que usamos antes
            this.fileContent = decodedContent
                .split('\n')
                .map((line: string) => `<p>${line}</p>`)
                .join('');

            this.isLoading = false;
        } catch (e) {
            // Se falhar (caso o texto já esteja ok), usa o original
            this.fileContent = fileData;
            this.isLoading = false;
        }

        this.displayTextViewer = true;
    }

    downloadImage(): void {
        //TODO: Implementar download da imagem exibida
        console.log('Download da imagem:', this.imageName);
    }

    private initializeForm(): void {
        this.directoryForm = this.formBuilder.group({
            name: ['', [Validators.required, noSpecialCharsValidator]]
        });
    }

    getParentDirectoryName(): string {
        if (this.breadcrumb.length < 2) return 'Meus Compartilhamentos';
        return this.breadcrumb[this.breadcrumb.length - 1].name.toUpperCase();
    }

    totalSizeFormatted() {
        const bytes = this.files$.reduce((acc, curr) => acc + (curr.contentLength || 0), 0);

        if (bytes === 0) return '0 B';

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    get completedUploadsCount(): number {
        return this.uploadProgress.filter((p) => p.progress === 100 || p.error).length;
    }

    get totalProgress(): number {
        if (!this.uploadProgress.length) return 0;
        const sum = this.uploadProgress.reduce((acc, curr) => acc + curr.progress, 0);
        return Math.round(sum / this.uploadProgress.length);
    }

    get isFolderUpload(): boolean {
        // Se algum arquivo tiver '/' no nome, é uma pasta
        return this.uploadProgress.some((p) => p.name.includes('/'));
    }

    startPollingIfNecessary() {
        // Cancela qualquer polling anterior antes de iniciar um novo
        this.stopPolling$.next();

        const hasPending = this.files$.some((f) => f.status === 'PENDING_UPLOAD');

        if (hasPending) {
            timer(5000, 10000) // Começa após 5s, repete a cada 10s
                .pipe(
                    // takeUntil garante que pare ao mudar de pasta ou destruir o componente
                    takeUntil(merge(this.destroy$, this.stopPolling$)),
                    tap(() => {
                        // Monta o objeto de evento baseado no estado atual da sua paginação
                        const event = {
                            first: this.filesFirst,
                            rows: this.filesRows
                        };
                        this.loadFiles(event);
                    }),
                    // Verifica se ainda existem arquivos pendentes após o último load
                    tap(() => {
                        const stillPending = this.files$.some((f) => f.status === 'PENDING_UPLOAD');
                        if (!stillPending) {
                            this.stopPolling$.next(); // Para o timer se tudo for SUCCESS
                        }
                    })
                )
                .subscribe();
        }
    }
}
